package kz.dentalcrm.app.screens.patients.detail

import com.kizitonwose.calendar.core.now
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.error_message
import dentalcrm.composeapp.generated.resources.snackbar_patient_deleted
import dentalcrm.composeapp.generated.resources.snackbar_patient_updated
import kotlinx.collections.immutable.toPersistentList
import kotlinx.coroutines.async
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import kz.dentalcrm.app.core.base.BaseStateViewModel
import kz.dentalcrm.app.core.ui.ScreenState
import kz.dentalcrm.app.core.utils.parseBackendDate
import kz.dentalcrm.app.core.utils.stateHandle
import kz.dentalcrm.app.data.network.response_model.PatientData
import kz.dentalcrm.app.domain.AppointmentRepository
import kz.dentalcrm.app.screens.patients.detail.mvi.PatientDetailSideEffect
import kz.dentalcrm.app.screens.patients.detail.mvi.PatientDetailState
import kz.dentalcrm.app.screens.patients.detail.mvi.PatientDetailViewEvent
import kz.dentalcrm.app.theme.SnackbarController
import kz.dentalcrm.app.theme.SnackbarEvent
import org.jetbrains.compose.resources.getString
import org.koin.core.component.inject
import kotlin.time.ExperimentalTime

class PatientDetailViewModel() : BaseStateViewModel<
        PatientDetailViewEvent,
        PatientDetailState,
        PatientDetailSideEffect>() {

    private val appointmentRepository: AppointmentRepository by inject()

    override fun setInitialState(): PatientDetailState = PatientDetailState(

    )

    @OptIn(ExperimentalTime::class)
    override fun handleEvents(event: PatientDetailViewEvent) {
        when (event) {
            is PatientDetailViewEvent.LoadData -> {
                loadData(id = event.id)
            }

            PatientDetailViewEvent.Delete -> {
                launch {
                    patientData?.let { patient ->
                        appointmentRepository.deletePatient(patient.id).stateHandle(
                            onError = { err, msg ->
                                SnackbarController.sendEvent(
                                    SnackbarEvent(
                                        getString(Res.string.error_message),
                                        SnackbarEvent.SnackbarType.Red
                                    )
                                )
                                setEffect {
                                    PatientDetailSideEffect.ShowError(
                                        msg ?: "Delete failed"
                                    )
                                }
                            },
                            onSuccess = {
                                SnackbarController.sendEvent(
                                    SnackbarEvent(
                                        getString(Res.string.snackbar_patient_deleted),
                                        SnackbarEvent.SnackbarType.Green
                                    )
                                )
                                setEffect { PatientDetailSideEffect.DeleteSuccess }
                            }
                        )
                    }
                }
            }

            PatientDetailViewEvent.Update -> {
                launch {
                    patientData?.let { patient ->
                        appointmentRepository.updatePatient(patient).stateHandle(
                            onFailure = {
                                SnackbarController.sendEvent(
                                    SnackbarEvent(
                                        getString(Res.string.error_message),
                                        SnackbarEvent.SnackbarType.Red
                                    )
                                )
                            },
                            onError = { err, msg ->
                                SnackbarController.sendEvent(
                                    SnackbarEvent(
                                        getString(Res.string.error_message),
                                        SnackbarEvent.SnackbarType.Red
                                    )
                                )
                                setEffect {
                                    PatientDetailSideEffect.ShowError(
                                        msg ?: "Update failed"
                                    )
                                }
                            },
                            onSuccess = {
                                SnackbarController.sendEvent(
                                    SnackbarEvent(
                                        getString(Res.string.snackbar_patient_updated),
                                        SnackbarEvent.SnackbarType.Green
                                    )
                                )
                                setEffect { PatientDetailSideEffect.UpdateSuccess }
                            }
                        )
                    }
                }
            }

            is PatientDetailViewEvent.UpdateAddress -> {
                val copyPatient = patientData
                setState {
                    copy(
                        patient = copyPatient?.copy(address = event.value)
                    )
                }
            }

            is PatientDetailViewEvent.UpdateBirthday -> {
                val copyPatient = patientData
                setState {
                    copy(
                        patient = copyPatient?.copy(
                            birth_date = event.formatted
                        ),
                        birth_day_selected = event.value.parseBackendDate() ?: LocalDate.now()
                    )
                }
            }

            is PatientDetailViewEvent.UpdateCity -> {
                val copyPatient = patientData
                setState {
                    copy(
                        patient = copyPatient?.copy(city = event.value)
                    )
                }
            }

            is PatientDetailViewEvent.UpdateIdn -> {
                val copyPatient = patientData
                setState {
                    copy(
                        patient = copyPatient?.copy(id_card = event.value)
                    )
                }
            }

            is PatientDetailViewEvent.UpdateIdnNumber -> {
                val copyPatient = patientData
                setState {
                    copy(
                        patient = copyPatient?.copy(id_number = event.value)
                    )
                }
            }

            is PatientDetailViewEvent.UpdateName -> {
                val copyPatient = patientData
                setState {
                    copy(
                        patient = copyPatient?.copy(name = event.value)
                    )
                }
            }

            is PatientDetailViewEvent.UpdatePhone -> {
                val copyPatient = patientData
                setState {
                    copy(
                        patient = copyPatient?.copy(phone = event.value)
                    )
                }
            }

            is PatientDetailViewEvent.UpdatePosition -> {
                val copyPatient = patientData
                setState {
                    copy(
                        patient = copyPatient?.copy(position = event.value)
                    )
                }
            }

            is PatientDetailViewEvent.UpdateSurname -> {
                val copyPatient = patientData
                setState {
                    copy(
                        patient = copyPatient?.copy(surname = event.value)
                    )
                }
            }

            is PatientDetailViewEvent.UpdateWorkplace -> {
                val copyPatient = patientData
                setState {
                    copy(
                        patient = copyPatient?.copy(workplace = event.value)
                    )
                }
            }

            is PatientDetailViewEvent.UpdatePatronymic -> {
                val copyPatient = patientData
                setState {
                    copy(
                        patient = copyPatient?.copy(patronymic = event.value)
                    )
                }
            }

            is PatientDetailViewEvent.UpdateGender -> {
                val copyPatient = patientData
                setState {
                    copy(
                        patient = copyPatient?.copy(gender = event.value)
                    )
                }
            }
        }
    }

    val patientData: PatientData?
        get() = viewState.value.patient

    private fun loadData(id: Int) {
        setState { copy(screenState = ScreenState.Loading) }
        launch {
            val patientDeferred = async { appointmentRepository.getPatient(id) }
            val appointmentsDeferred =
                async { appointmentRepository.getPatientLastAppointments(id) }

            val patientResult = patientDeferred.await()
            val appointmentsResult = appointmentsDeferred.await()

            patientResult.stateHandle(
                onError = { _, msg ->
                    setEffect { PatientDetailSideEffect.ShowError(msg ?: "Failed to load patient") }
                },
                onSuccess = { patient ->
                    setState { copy(patient = patient) }
                }
            )

            appointmentsResult.stateHandle(
                onError = { _, _ -> },
                onSuccess = { appointments ->
                    setState { copy(patientAppointments = appointments.toPersistentList()) }
                }
            )

            setState { copy(screenState = ScreenState.Idle) }
        }
    }
}